import React from "react";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ReactDOM from "react-dom";
import { createRoot } from "react-dom/client";

import { ErrorBoundary, router } from "@/app/router";

import "../css/app.css";
import "./config/bootstrap";

import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { NuqsAdapter } from "nuqs/adapters/react-router";
import { RouterProvider } from "react-router-dom";
import { addLocale } from "ttag";

import { env } from "@/config/env";
import { ANALYTICS_PARAMS } from "@/features/analytics/models";
import { CustomerIoContextProvider } from "@/features/analytics/providers";
import { initGoogleTagManagerTracking } from "@/features/analytics/triggers";
import { useInitLanguage } from "@/shared/hooks";
import {
  isInAppBrowserEvent,
  queryClient,
  SENTRY_DENY_URLS,
  SENTRY_IGNORE_ERRORS,
} from "@/shared/lib";
import { getAttributionCookie } from "@/shared/lib/attribution-cookie";
import { Toasts } from "@/shared/ui";
import translationEs from "../lang/es/es.po.json";

Sentry.init({
  dsn: env.VITE_SENTRY_DSN_PUBLIC,
  environment: env.VITE_SENTRY_ENVIRONMENT,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  tracesSampleRate: 0,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  ignoreErrors: SENTRY_IGNORE_ERRORS,
  denyUrls: SENTRY_DENY_URLS,
  beforeSend: (event) => (isInAppBrowserEvent(event) ? null : event),
});

addLocale("es", translationEs);
const stripePromise = loadStripe(env.VITE_STRIPE_PUBLIC_KEY);

const customerIoAnalytics = AnalyticsBrowser.load({
  writeKey: env.VITE_CUSTOMER_IO_WRITE_KEY,
});

const cioId =
  new URLSearchParams(window.location.search).get(
    ANALYTICS_PARAMS.CUSTOMER_IO_ID,
  ) ?? getAttributionCookie()?.cio_id;

if (cioId) {
  void customerIoAnalytics.setAnonymousId(cioId);
}

function AppWrapper() {
  const language = useInitLanguage();

  initGoogleTagManagerTracking();

  return (
    <NuqsAdapter>
      <Elements stripe={stripePromise} options={{ locale: language }}>
        <CustomerIoContextProvider analytics={customerIoAnalytics}>
          <RouterProvider key={language} router={router} />
        </CustomerIoContextProvider>
      </Elements>
    </NuqsAdapter>
  );
}

let container: ReturnType<typeof document.getElementById> = null;

document.addEventListener("DOMContentLoaded", () => {
  if (!container) {
    container = document.getElementById("app")!;
    const root = createRoot(container);
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <Sentry.ErrorBoundary fallback={<ErrorBoundary />}>
            <AppWrapper />
          </Sentry.ErrorBoundary>

          {ReactDOM.createPortal(<Toasts />, document.body)}

          {env.VITE_APP_ENV === "local" && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </React.StrictMode>,
    );
  }
});
